<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <v-btn
          text
          color="deep-purple lighten-1"
          @click="goBack"
          class="mr-4"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('diagnostics.back_to_list') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="diagnostic">
      <v-col cols="12">
        <v-card class="pa-4 elevation-1">
          <v-card-title class="text-h5 deep-purple--text">
            {{ diagnostic.nombre }}
          </v-card-title>
          <v-card-subtitle>
            <v-row>
              <v-col cols="auto">
                <span class="font-weight-bold">{{ $t('diagnostics.evaluation') }}:</span>
                {{ diagnostic.id }}
              </v-col>
              <v-col cols="auto">
                <span class="font-weight-bold">{{ $t('diagnostics.grade') }}:</span>
                {{ diagnostic.grado }}°
              </v-col>
              <v-col cols="auto">
                <span class="font-weight-bold">{{ $t('diagnostics.level') }}:</span>
                {{ $t(`diagnostics.levels.${diagnostic.nivel}`) }}
              </v-col>
              <v-col cols="auto">
                <span class="font-weight-bold">{{ $t('diagnostics.country') }}:</span>
                {{ countryFlag }} {{ countryName }}
              </v-col>
            </v-row>
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="pa-3 elevation-1">
          <div class="d-flex flex-wrap align-center">
            <span class="text-subtitle-2 font-weight-bold mr-3">
              {{ $t('diagnostics.country') }}:
            </span>
            <v-select
              :items="countryOptions"
              :value="countryCode"
              item-text="name"
              item-value="code"
              dense
              outlined
              hide-details
              class="country-selector mr-3"
              @change="handleCountryChange"
            >
              <template v-slot:selection="{ item }">
                <span>{{ item.flag }} {{ item.name }}</span>
              </template>
              <template v-slot:item="{ item }">
                <span>{{ item.flag }} {{ item.name }}</span>
              </template>
            </v-select>

          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <h3 class="text-h6 mb-3">{{ $t('diagnostics.questions') }}</h3>
        <diagnostic-questions-table
          :loading="loading"
          :questions="enrichedQuestions"
          @edit-question="openEditModal"
        />
      </v-col>
    </v-row>

    <edit-question-variant-modal
      v-model="editModalOpen"
      :question="selectedQuestion"
      :original-question="selectedOriginalQuestion"
      :variant="selectedVariant"
      :country-code="countryCode"
      @saved="handleVariantSaved"
    />
  </v-container>
</template>

<script>
import diagnosticService from '@/services/diagnostic.service';
import diagnosticCountryService from '@/services/diagnosticCountry.service';
import { getCountryByCode, SUPPORTED_COUNTRIES } from '@/constants/diagnostics';
import DiagnosticQuestionsTable from '@/components/diagnostics/DiagnosticQuestionsTable.vue';
import EditQuestionVariantModal from '@/components/diagnostics/modals/EditQuestionVariantModal.vue';
import snackbarMixin from '@/mixins/snackbar';
import { canAccessDiagnosticCustomization } from '@/utils/diagnosticPermissions';

export default {
  name: 'DiagnosticQuestions',
  components: {
    DiagnosticQuestionsTable,
    EditQuestionVariantModal,
  },
  mixins: [snackbarMixin],
  props: {
    diagnosticId: { type: [String, Number], required: true },
  },
  data() {
    return {
      loading: false,
      diagnostic: null,
      questions: [],
      generalQuestionsMap: {},
      variants: [],
      variantsMap: {},
      countryCode: 'GENERAL',
      editModalOpen: false,
      selectedQuestion: null,
      selectedOriginalQuestion: null,
      selectedVariant: null,
      countryOptions: [
        { code: 'GENERAL', name: 'General', flag: '🌐' },
        ...SUPPORTED_COUNTRIES,
      ],
    };
  },
  computed: {
    isAllowedUser() {
      return canAccessDiagnosticCustomization(this.$store.getters.currentUserEmail);
    },
    country() {
      return getCountryByCode(this.countryCode);
    },
    countryName() {
      if (this.countryCode === 'GENERAL') {
        return 'General';
      }

      return this.country ? this.country.name : this.countryCode;
    },
    countryFlag() {
      if (this.countryCode === 'GENERAL') {
        return '🌐';
      }

      return this.country ? this.country.flag : '';
    },
    enrichedQuestions() {
      return this.questions.map((q, index) => ({
        ...q,
        orden: q.item || (index + 1),
        skill: this.getSkillLabel(q),
      }));
    },
  },
  async mounted() {
    if (!this.isAllowedUser) {
      this.snackbarError(this.$t('diagnostics.no_permission'));
      this.$router.push('/dashboard/colleges');
      return;
    }
    this.countryCode = this.$route.query.country || 'GENERAL';
    await this.loadDataForSelectedVersion();
  },
  methods: {
    isGeneralVersion() {
      return this.countryCode === 'GENERAL';
    },
    getResponseContent(payload) {
      if (!payload) {
        return null;
      }

      if (payload.content !== undefined) {
        return payload.content;
      }

      return payload;
    },
    normalizeDiagnostic(diagnostic) {
      if (!diagnostic) {
        return null;
      }

      const preguntas = this.sortQuestionsAndAnswers(
        diagnostic.preguntas || diagnostic.questions || [],
      );

      return {
        ...diagnostic,
        nombre: diagnostic.nombre || diagnostic.name || '',
        grado: diagnostic.grado || diagnostic.grade || null,
        nivel: diagnostic.nivel || diagnostic.diagnosticLevel || null,
        preguntas,
      };
    },
    sortQuestionsAndAnswers(questions) {
      return [...questions]
        .map((question) => ({
          ...question,
          respuestas: this.sortRespuestas(question.respuestas || []),
        }))
        .sort((left, right) => (left.item || 0) - (right.item || 0));
    },
    sortRespuestas(respuestas) {
      return [...respuestas].sort((left, right) => (left.item || 0) - (right.item || 0));
    },
    getSkillLabel(question) {
      if (!question) {
        return 'N/A';
      }

      if (question.skill) {
        return question.skill;
      }

      if (question.aimOfAssessmentDTO?.code) {
        return question.aimOfAssessmentDTO.code.replace('.', ' ');
      }

      if (question.aimOfAssessmentDTO?.measuredAspect && question.aimOfAssessmentDTO?.subCode) {
        return `${question.aimOfAssessmentDTO.measuredAspect} ${question.aimOfAssessmentDTO.subCode}`;
      }

      if (question.aimOfAssessmentDTO?.subCode) {
        return String(question.aimOfAssessmentDTO.subCode);
      }

      if (question.aimOfAssessmentDTO?.detail) {
        return question.aimOfAssessmentDTO.detail;
      }

      if (question.lbAimOfassessmentId) {
        return `Skill #${question.lbAimOfassessmentId}`;
      }

      return 'N/A';
    },
    extractVariants(payload) {
      if (Array.isArray(payload?.content)) {
        return payload.content;
      }

      if (Array.isArray(payload)) {
        return payload;
      }

      return [];
    },
    buildVariantsMap(variants) {
      const map = {};
      variants.forEach((variant) => {
        map[variant.preguntaId] = variant;
      });
      return map;
    },
    buildQuestionsMap(questions) {
      const map = {};
      (questions || []).forEach((question) => {
        map[question.id] = question;
      });
      return map;
    },

    async loadVariants() {
      if (this.isGeneralVersion()) {
        this.variants = [];
        this.variantsMap = {};
        return;
      }

      try {
        const { data } = await diagnosticCountryService.getVariantsByDiagnostico(
          Number(this.diagnosticId),
          this.countryCode,
        );

        const variants = this.extractVariants(data);

        if (variants.length > 0) {
          this.variants = variants;
          this.variantsMap = this.buildVariantsMap(variants);
        } else {
          this.variants = [];
          this.variantsMap = {};
        }
      } catch (_error) {
        // Don't show error - variants might not exist yet
        this.variants = [];
        this.variantsMap = {};
      }
    },

    async loadDataForSelectedVersion() {
      const requestedCountry = this.countryCode;
      this.loading = true;
      try {
        const { data: generalData } = await diagnosticService.getDiagnosticQuestions(
          Number(this.diagnosticId),
        );

        if (this.countryCode !== requestedCountry) {
          return;
        }

        const generalDiagnostic = this.normalizeDiagnostic(this.getResponseContent(generalData));

        if (!generalDiagnostic) {
          this.questions = [];
          this.generalQuestionsMap = {};
          return;
        }

        this.generalQuestionsMap = this.buildQuestionsMap(generalDiagnostic.preguntas || []);

        if (this.isGeneralVersion()) {
          this.diagnostic = generalDiagnostic;
          this.questions = generalDiagnostic.preguntas || [];
        } else {
          const { data: countryData } = await diagnosticService.getDiagnosticQuestions(
            Number(this.diagnosticId),
            this.countryCode,
          );

          if (this.countryCode !== requestedCountry) {
            return;
          }

          const countryDiagnostic = this.normalizeDiagnostic(this.getResponseContent(countryData));
          this.diagnostic = countryDiagnostic || generalDiagnostic;
          this.questions = (countryDiagnostic && countryDiagnostic.preguntas)
            || generalDiagnostic.preguntas
            || [];
        }
      } catch (_error) {
        this.snackbarError(this.$t('general.error_msg'));
      } finally {
        if (this.countryCode === requestedCountry) {
          this.loading = false;
        }
      }

      await this.loadVariants();
    },

    openEditModal(question) {
      this.selectedQuestion = question;
      this.selectedOriginalQuestion = this.generalQuestionsMap[question.id] || question;
      this.selectedVariant = this.variantsMap[question.id] || null;
      this.editModalOpen = true;
    },

    async handleVariantSaved() {
      await this.loadDataForSelectedVersion();
    },

    async handleCountryChange(value) {
      const nextCountry = value || 'GENERAL';
      if (nextCountry === this.countryCode) {
        return;
      }

      if (this.editModalOpen) {
        this.editModalOpen = false;
      }

      this.countryCode = nextCountry;
      await this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          country: nextCountry,
        },
      });
      await this.loadDataForSelectedVersion();
    },

    goBack() {
      this.$router.push('/dashboard/diagnostics');
    },
  },
};
</script>

<style scoped>
.v-card-subtitle {
  opacity: 0.8;
}

.country-selector {
  max-width: 260px;
  min-width: 220px;
}
</style>
